import React, { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useCurrentUser } from '@/hooks/useAuth';
import { useRestaurant } from '@/hooks/useRestaurants';
import { Logo } from '@/components/common/Logo';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, AlertCircle, BookOpen } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { supabase } from '@/lib/supabase';

export default function Welcome() {
  const navigate = useNavigate();
  const { data: user, isLoading: isLoadingUser } = useCurrentUser();
  const {
    data: restaurant,
    isLoading: isLoadingRestaurant,
    error: restaurantError,
  } = useRestaurant(user?.restaurantId || '');

  // Track auth state to properly handle redirects
  const [isAuthChecked, setIsAuthChecked] = React.useState(false);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);

  // Check authentication and user type
  useEffect(() => {
    const checkAuth = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user) {
        // Not authenticated - redirect to login
        setIsAuthenticated(false);
        setIsAuthChecked(true);
        navigate({ to: '/' });
        return;
      }

      setIsAuthenticated(true);
      setIsAuthChecked(true);

      // Redirect non-admin users to their appropriate dashboards
      const userType = session.user.user_metadata?.['user_type'];
      if (userType === 'assistant') {
        navigate({ to: '/assistant-dashboard' });
        return;
      }

      if (session.user.user_metadata?.['role']) {
        const role = session.user.user_metadata['role'];
        if (role === 'server') {
          navigate({ to: '/staff-server' });
        } else if (role === 'kitchen') {
          navigate({ to: '/staff-kitchen' });
        }
      }
    };
    checkAuth();
  }, [navigate]);

  // Show loading while checking auth or fetching user/restaurant data
  if (!isAuthChecked || isLoadingUser || (user?.restaurantId && isLoadingRestaurant)) {
    return <LoadingSpinner fullScreen message="جاري التحميل..." />;
  }

  // If not authenticated after check, show loading (redirect to / is happening)
  if (!isAuthenticated) {
    return <LoadingSpinner fullScreen message="جاري التحميل..." />;
  }

  // If user has no restaurant ID, show error and manual creation option
  if (!user?.restaurantId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-secondary/10 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="items-center">
            <Logo className="h-24 w-24 mb-4" />
            <CardTitle className="text-2xl">لم يتم العثور على المطعم</CardTitle>
            <CardDescription>
              يجب إنشاء مطعم تلقائيًا لحسابك. إذا لم يحدث ذلك، يمكنك إنشاؤه يدويًا.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3 p-4 rounded-lg border bg-destructive/10 border-destructive/20">
              <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-destructive mb-1">
                  لم يتم إنشاء المطعم تلقائيًا
                </p>
                <p className="text-sm text-muted-foreground">
                  يمكنك إنشاء مطعم الآن أو الاتصال بالدعم للمساعدة.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If restaurant fetch failed, show error with retry
  if (restaurantError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-secondary/10 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="items-center">
            <Logo className="h-24 w-24 mb-4" />
            <CardTitle className="text-2xl">خطأ في تحميل المطعم</CardTitle>
            <CardDescription>حدث خطأ أثناء تحميل معلومات مطعمك</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3 p-4 rounded-lg border bg-destructive/10 border-destructive/20">
              <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">
                  {restaurantError.message || 'فشل تحميل بيانات المطعم'}
                </p>
              </div>
            </div>

            <Button onClick={() => window.location.reload()} className="w-full">
              إعادة المحاولة
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show welcome screen with restaurant info
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-secondary/10 p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="items-center text-center">
          <Logo className="h-24 w-24 mb-4" />
          <CardTitle className="text-3xl mb-2">مرحبا بك في ديجي مطعم، {user?.name}!</CardTitle>
          <CardDescription className="text-base">مطعمك جاهز للاستخدام</CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Restaurant Created Successfully */}
          <div className="flex items-start gap-3 p-4 rounded-lg border bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900">
            <Check className="h-5 w-5 text-green-600 dark:text-green-500 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-green-900 dark:text-green-100 mb-1">
                تم إنشاء مطعمك تلقائيًا
              </p>
              <p className="text-sm text-green-700 dark:text-green-300">
                تم إعداد كل شيء وجاهز للبدء
              </p>
            </div>
          </div>

          {/* Restaurant Information */}
          {restaurant && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">معلومات المطعم</h3>
              <div className="rounded-lg border p-4 space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">اسم المطعم</p>
                  <p className="font-medium text-lg">{restaurant.name}</p>
                </div>

                {restaurant.uniqueIdentifier && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">المعرف الفريد</p>
                    <p className="font-mono text-sm bg-muted px-3 py-2 rounded">
                      {restaurant.uniqueIdentifier}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      سيتم استخدام هذا المعرف في عناوين البريد الإلكتروني للموظفين
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Quick Setup Checklist */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold">الخطوات التالية</h3>
            <div className="rounded-lg border p-4 space-y-2">
              <p className="text-sm text-muted-foreground mb-3">يمكنك البدء في إعداد مطعمك:</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-muted-foreground/50" />
                  إضافة تفاصيل المطعم (العنوان، الهاتف)
                </li>
                <li className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-muted-foreground/50" />
                  إنشاء الطاولات
                </li>
                <li className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-muted-foreground/50" />
                  إعداد المخزون
                </li>
                <li className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-muted-foreground/50" />
                  إعداد القائمة
                </li>
                <li className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-muted-foreground/50" />
                  إضافة الموظفين
                </li>
              </ul>
            </div>
          </div>

          {/* Tutorial Link */}
          <Link to="/tutorial">
            <Button className="w-full gap-2" size="lg">
              <BookOpen className="h-5 w-5" />
              دليل الاستخدام - كيفية إعداد مطعمك
            </Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
